import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Button, Row, Col, Table, Alert, Card, Badge } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "src/redux";
import { fetchTls } from "src/redux/reducers/TransactionManager";
import { createAlert } from "src/redux/reducers/Alerts";
import { IDatasource } from "ag-grid-community";
import AgBulkTable from "../Analyze/AnalyzeTransactionsTable/AgBulkTable";
import { convertFilterModel, convertSortModel } from "src/utils/filters";
import { datefilterParamsYYYYMMDD } from "../Analyze/AnalyzeTransactionsTable";
import {
  EditableFieldText,
  EditableFieldAmount,
  EditableFieldDateYYYYMMDD,
} from "src/utils/EditableField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useHasQbo } from "src/utils/utils";
import ExternalFilter from "src/views/Docuclipper/ExternalFilter";
import {
  setExternalFiltersColumnDefs,
  clearExternalFilters,
} from "src/redux/reducers/ExternalFilter";
import {
  createTransactionLineMatch,
  getTransactionLineMatches,
  deleteTransactionLineMatch,
  getQboBankTransactionMatch,
  searchQboBankTransactions,
  confirmQboBankTransactionMatch,
  removeQboBankTransactionMatch,
  getXeroBankTransactionMatch,
  searchXeroBankTransactions,
  confirmXeroBankTransactionMatch,
  removeXeroBankTransactionMatch,
} from "src/docuclipper/api";
import { Invoice2 } from "src/docuclipper/DocuclipperTypes";

interface Props {
  isOpen: boolean;
  onToggle: () => void;
  invoiceTransactionLine?: any;
  invoice?: Invoice2; // Add full invoice object
  onMatchesUpdated?: () => void;
  integrationId?: number;
  integrationProvider?: "quickbooks" | "xero" | null;
}

interface TransactionLineMatch {
  id: number;
  sourceTransactionLineId: number;
  destinationTransactionLineId: number;
  status: "pending" | "approved" | "rejected";
  metadata?: any;
  createdAt: string;
  updatedAt: string;
  DestinationTransactionLine?: any;
  bankExternalId?: string;
}

const InvoiceTransactionMatcher: React.FC<Props> = ({
  isOpen,
  onToggle,
  invoiceTransactionLine,
  invoice,
  onMatchesUpdated,
  integrationId,
  integrationProvider,
}) => {
  const dispatch = useDispatch();
  const gridApi = useRef<any>(null);
  const [selectedBankTransactions, setSelectedBankTransactions] = useState<
    any[]
  >([]);
  const [existingMatches, setExistingMatches] = useState<
    TransactionLineMatch[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMatches, setIsLoadingMatches] = useState(false);
  const [providerSearchTerm, setProviderSearchTerm] = useState("");
  const [providerCandidates, setProviderCandidates] = useState<any[]>([]);
  const [providerSearching, setProviderSearching] = useState(false);
  const providerMode =
    !!integrationId &&
    (integrationProvider === "quickbooks" || integrationProvider === "xero");
  const topProviderCandidate =
    providerCandidates && providerCandidates.length > 0
      ? providerCandidates[0]
      : null;
  const topProviderConfidence = Math.round(
    Number(topProviderCandidate?.confidenceScore || 0) * 100
  );

  const { tls, pagination } = useSelector(
    (state: ReduxState) => state.TransactionManager
  );
  const job = useSelector((state: ReduxState) => state.JobData.job);
  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );
  const { externalFilters } = useSelector(
    (state: ReduxState) => state.ExternalFilter
  );
  const hasQbo = useHasQbo();

  // Load existing matches when component opens
  useEffect(() => {
    if (isOpen && invoiceTransactionLine?.id) {
      loadExistingMatches();
    }
  }, [isOpen, invoiceTransactionLine?.id]);

  const loadExistingMatches = async () => {
    setIsLoadingMatches(true);
    try {
      if (providerMode) {
        const response =
          integrationProvider === "quickbooks"
            ? await getQboBankTransactionMatch(
                Number(integrationId),
                Number(invoiceTransactionLine.id)
              )
            : await getXeroBankTransactionMatch(
                Number(integrationId),
                Number(invoiceTransactionLine.id)
              );
        setProviderCandidates(
          (response?.candidates || []).map((c: any) => ({
            id: c.bankTransactionId,
            account: c.accountName || "N/A",
            date: c.date || "N/A",
            description: c.description || c.payee || "N/A",
            amount: c.amount || 0,
            confidenceScore: c.confidenceScore || 0,
          }))
        );
        const selected = Array.isArray(response?.selectedMatches)
          ? response.selectedMatches
          : [];
        setExistingMatches(
          selected.map((m: any, idx: number) => ({
            id: idx + 1,
            sourceTransactionLineId: Number(invoiceTransactionLine.id),
            destinationTransactionLineId: 0,
            status: m.status || m.matchStatus || response.matchStatus || "approved",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            bankExternalId: String(m.bankTransactionId || m.bankExternalId || ""),
            DestinationTransactionLine: {
              account: m.accountName,
              date: m.date,
              description: m.description || m.payee || "",
              amount: m.amount,
            },
          }))
        );
        return;
      }
      const matches = await getTransactionLineMatches(
        "invoice2bank",
        Number(invoiceTransactionLine.id)
      );
      setExistingMatches(matches.matches || []);
    } catch (error) {
      console.error("Error loading existing matches:", error);
      dispatch(
        createAlert({
          message: "Error loading existing matches",
          type: "error",
          timeout: 5000,
        })
      );
    } finally {
      setIsLoadingMatches(false);
    }
  };

  const searchProviderBankTransactions = useCallback(
    async (term: string) => {
      if (!providerMode) return;
      setProviderSearching(true);
      try {
        const response =
          integrationProvider === "quickbooks"
            ? await searchQboBankTransactions(
                Number(integrationId),
                Number(invoiceTransactionLine.id),
                term
              )
            : await searchXeroBankTransactions(
                Number(integrationId),
                Number(invoiceTransactionLine.id),
                term
              );
        const mapped = (response?.candidates || []).map((c: any) => ({
          id: c.bankTransactionId,
          account: c.accountName || "N/A",
          date: c.date || "N/A",
          description: c.description || c.payee || "N/A",
          amount: c.amount || 0,
          confidenceScore: c.confidenceScore || 0,
        }));
        setProviderCandidates(mapped);
      } finally {
        setProviderSearching(false);
      }
    },
    [providerMode, integrationProvider, integrationId, invoiceTransactionLine?.id]
  );

  // External filter columns configuration
  const externalFilterColumns = useMemo(
    () => [
      {
        field: "account",
        label: "Account",
        filter: true,
        filterType: "text",
        defaultFilterType: "contains",
        configurable: true,
      },
      {
        field: "date",
        label: "Date",
        filter: true,
        filterType: "date",
        defaultFilterType: "equals",
        configurable: true,
      },
      {
        field: "description",
        label: "Description",
        filter: true,
        configurable: true,
        filterType: "text",
        defaultFilterType: "contains",
      },
      {
        field: "amount",
        label: "Amount",
        filter: true,
        filterType: "number",
        defaultFilterType: "equals",
        configurable: true,
      },
    ],
    []
  );

  // Column definitions for bank transactions table
  const columnDefs = useMemo(
    () => [
      {
        field: "account",
        headerName: "Account",
        minWidth: 150,
        index: 0,
      },
      {
        field: "date",
        headerName: "Date",
        filter: "agDateColumnFilter",
        filterParams: datefilterParamsYYYYMMDD,
        sortable: true,
        minWidth: 120,
        index: 1,
      },
      {
        field: "description",
        headerName: "Description",
        minWidth: 300,
        index: 2,
      },
      {
        field: "amount",
        headerName: "Amount",
        sortable: true,
        minWidth: 150,
        filter: "agNumberColumnFilter",
        cellRenderer: (props) => {
          if (!props.data) return "";
          return props.data.amount
            ? `$${parseFloat(props.data.amount).toFixed(2)}`
            : "";
        },
        index: 3,
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      filter: true,
      sortable: true,
    }),
    []
  );

  // Fetch bank transactions
  const getRows = useCallback(
    (params) => {
      dispatch(
        fetchTls(false, {
          pagination: {
            page: Math.floor(params.startRow / pagination.pageSize) + 1,
            pageSize: params.endRow - params.startRow,
          },
          filters: [
            {
              field: "fieldName",
              operator: "equals" as const,
              value: "bankMode",
            },
            ...convertFilterModel(params),
          ],
          sorting: convertSortModel(params.sortModel),
          successCallback: params.successCallback,
          documents: tls.documents,
          hasQbo,
          job,
          l1,
          l2,
        })
      );
    },
    [
      dispatch,
      pagination.pageSize,
      tls.documents,
      hasQbo,
      job,
      l1,
      l2,
      externalFilters,
    ]
  );

  const dataSource: IDatasource = useMemo(
    () => ({
      rowCount: undefined,
      getRows,
    }),
    [getRows]
  );

  //   console.log({ invoice });
  useEffect(() => {
    if (gridApi.current && isOpen) {
      gridApi.current.setDatasource(dataSource);
    }
  }, [dataSource, isOpen, externalFilters]);

  // Initialize external filters
  useEffect(() => {
    if (isOpen) {
      dispatch(clearExternalFilters());
      dispatch(
        setExternalFiltersColumnDefs({
          externalFilterColumnDefs: columnDefs,
        })
      );
    }
  }, [isOpen, columnDefs, dispatch]);

  // Handle transaction selection
  const handleTransactionSelected = (selectedRows: any[]) => {
    // console.log("handleTransactionSelected called with:", selectedRows);
    // console.log("Current selectedBankTransactions:", selectedBankTransactions);
    // console.log("Current existingMatches:", existingMatches);

    // Add new selections to the matches table
    const newTransactions = selectedRows.filter((row) => {
      const alreadySelected = selectedBankTransactions.find(
        (existing) => existing.id === row.id
      );
      const alreadyMatched = existingMatches.find(
          (match) =>
            match.destinationTransactionLineId === row.id ||
            String(match.bankExternalId || "") === String(row.id)
      );

      //   console.log(
      //     `Row ${
      //       row.id
      //     }: alreadySelected=${!!alreadySelected}, alreadyMatched=${!!alreadyMatched}`
      //   );

      return !alreadySelected && !alreadyMatched;
    });

    // console.log("Filtered newTransactions:", newTransactions);

    if (newTransactions.length > 0) {
      setSelectedBankTransactions((prev) => {
        const updated = [...prev, ...newTransactions];
        // console.log("Updated selectedBankTransactions:", updated);
        return updated;
      });
    } else {
      //   console.log("No new transactions to add - all filtered out");
    }
  };

  // Remove transaction from new selections
  const handleRemoveTransaction = (transactionId: string) => {
    setSelectedBankTransactions((prev) =>
      prev.filter((t) => t.id !== transactionId)
    );
  };

  // Delete existing match
  const handleDeleteExistingMatch = async (matchId: number, bankExternalId?: string) => {
    try {
      if (providerMode) {
        if (integrationProvider === "quickbooks") {
          await removeQboBankTransactionMatch(
            Number(integrationId),
            Number(invoiceTransactionLine.id),
            bankExternalId
          );
        } else {
          await removeXeroBankTransactionMatch(
            Number(integrationId),
            Number(invoiceTransactionLine.id),
            bankExternalId
          );
        }
        await loadExistingMatches();
      } else {
      await deleteTransactionLineMatch(matchId);

      // Remove from local state
      setExistingMatches((prev) =>
        prev.filter((match) => match.id !== matchId)
      );
      }

      // Notify parent component
      if (onMatchesUpdated) {
        onMatchesUpdated();
      }

      dispatch(
        createAlert({
          message: "Match deleted successfully",
          type: "success",
          timeout: 3000,
        })
      );
    } catch (error) {
      console.error("Error deleting match:", error);
      dispatch(
        createAlert({
          message: "Error deleting match",
          type: "error",
          timeout: 5000,
        })
      );
    }
  };

  // Create matches using real API
  const handleCreateMatch = async () => {
    if (!invoiceTransactionLine || selectedBankTransactions.length === 0) {
      dispatch(
        createAlert({
          message: "Please select at least one bank transaction to match",
          type: "error",
          timeout: 5000,
        })
      );
      return;
    }

    setIsLoading(true);

    try {
      if (providerMode) {
        const first = selectedBankTransactions[0];
        if (!first) return;
        if (integrationProvider === "quickbooks") {
          await confirmQboBankTransactionMatch({
            integrationId: Number(integrationId),
            transactionId: Number(invoiceTransactionLine.id),
            bankTransactionId: String(first.id),
          });
        } else {
          await confirmXeroBankTransactionMatch({
            integrationId: Number(integrationId),
            transactionId: Number(invoiceTransactionLine.id),
            bankTransactionId: String(first.id),
          });
        }
        await loadExistingMatches();
        setSelectedBankTransactions([]);
        if (onMatchesUpdated) onMatchesUpdated();
        dispatch(
          createAlert({
            message: "Successfully matched invoice to bank transaction",
            type: "success",
            timeout: 5000,
          })
        );
        return;
      }
      // Prepare matches array
      const matchesToCreate = selectedBankTransactions.map((transaction) => ({
        sourceTransactionLineId: Number(invoiceTransactionLine.id),
        destinationTransactionLineId: Number(transaction.id),
      }));

      //   console.log("Creating matches:", matchesToCreate);

      // Create all matches at once
      const response = await createTransactionLineMatch(
        matchesToCreate,
        "invoice2bank",
        {
          matchedAt: new Date().toISOString(),
          matchedBy: "user",
        }
      );

      //   console.log("API response:", response);

      // Handle response - the API might return different structures
      let newMatches: TransactionLineMatch[] = [];
      if (Array.isArray(response)) {
        newMatches = response;
      } else if (response && Array.isArray(response.matches)) {
        newMatches = response.matches;
      } else if (response && response.match) {
        newMatches = Array.isArray(response.match)
          ? response.match
          : [response.match];
      } else if (response) {
        newMatches = [response];
      }

      //   console.log("Processed matches:", newMatches);

      // Reload existing matches to get complete data including DestinationTransactionLine
      await loadExistingMatches();

      // Clear selected transactions
      setSelectedBankTransactions([]);

      // Notify parent component
      if (onMatchesUpdated) {
        onMatchesUpdated();
      }

      dispatch(
        createAlert({
          message: `Successfully matched invoice transaction to ${selectedBankTransactions.length} bank transaction(s)`,
          type: "success",
          timeout: 5000,
        })
      );
    } catch (error) {
      console.error("Error creating matches:", error);
      dispatch(
        createAlert({
          message: `Error creating matches: ${
            error instanceof Error ? error.message : String(error)
          }`,
          type: "error",
          timeout: 5000,
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleProviderSelect = async (transaction: any) => {
    if (!providerMode || !invoiceTransactionLine) return;
    setIsLoading(true);
    try {
      if (integrationProvider === "quickbooks") {
        await confirmQboBankTransactionMatch({
          integrationId: Number(integrationId),
          transactionId: Number(invoiceTransactionLine.id),
          bankTransactionId: String(transaction.id),
        });
      } else {
        await confirmXeroBankTransactionMatch({
          integrationId: Number(integrationId),
          transactionId: Number(invoiceTransactionLine.id),
          bankTransactionId: String(transaction.id),
        });
      }
      await loadExistingMatches();
      setSelectedBankTransactions([]);
      if (onMatchesUpdated) onMatchesUpdated();
      dispatch(
        createAlert({
          message: "Successfully matched invoice to bank transaction",
          type: "success",
          timeout: 5000,
        })
      );
    } catch (error) {
      dispatch(
        createAlert({
          message: `Error creating match: ${
            error instanceof Error ? error.message : String(error)
          }`,
          type: "error",
          timeout: 5000,
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedBankTransactions([]);
    onToggle();
  };

  const totalMatches = existingMatches.length + selectedBankTransactions.length;

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1049,
          }}
          onClick={onToggle}
        />
      )}

      {/* Drawer */}
      <div
        className={`invoice-transaction-matcher-drawer ${
          isOpen ? "open" : "closed"
        }`}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          width: isOpen ? "70%" : "0",
          backgroundColor: "#fff",
          boxShadow: "-2px 0 10px rgba(0,0,0,0.1)",
          transition: "width 0.3s ease-in-out",
          zIndex: 1050,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer Header */}
        <div
          style={{
            padding: "1rem 1.5rem",
            borderBottom: "1px solid #dee2e6",
            backgroundColor: "#f8f9fa",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h5 className="mb-0">
            Match Invoice to Bank Transactions
            {totalMatches > 0 && (
              <Badge variant="primary" className="ml-2">
                {totalMatches} match{totalMatches !== 1 ? "es" : ""}
              </Badge>
            )}
            {providerMode && (
              <Badge variant="info" className="ml-2 text-uppercase">
                {integrationProvider}
              </Badge>
            )}
            {providerMode && topProviderConfidence > 0 && (
              <Badge variant="light" className="ml-2">
                {topProviderConfidence}% confidence
              </Badge>
            )}
          </h5>
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={onToggle}
              className="mr-2"
            >
              <FontAwesomeIcon
                icon={isOpen ? "chevron-right" : "chevron-left"}
              />
            </Button>
          </div>
        </div>

        {/* Drawer Content */}
        <div style={{ flex: 1, overflow: "auto", padding: "1.5rem" }}>
          {invoice && invoiceTransactionLine && (
            <Alert variant="info" className="mb-3">
              <div className="d-flex justify-content-between">
                <div>
                  <strong>Invoice #{invoice.id || "N/A"}</strong>
                  <div className="mt-1">
                    <small>
                      <strong>Date:</strong> {invoice.date || "N/A"} |
                      <strong> Total:</strong> $
                      {parseFloat(invoice.total || "0").toFixed(2)} |
                      <strong>
                        {" "}
                        {["receivables", "invoice"].includes(invoice.docType) &&
                          "Customer"}
                        {["payables", "bill", "expense"].includes(
                          invoice.docType
                        ) && "Vendor"}
                        :
                      </strong>{" "}
                      {invoice.docType === "receivables" &&
                        (invoice.customerExcelEdited ||
                          invoice.customerExcelAutomated ||
                          invoice.customerExcel ||
                          "N/A")}
                      {invoice.docType === "payables" &&
                        (invoice.vendorExcelEdited ||
                          invoice.vendorExcelAutomated ||
                          invoice.vendorExcel ||
                          "N/A")}
                      {["bill", "expense"].includes(invoice.docType) &&
                        (invoice.vendorEditedName ||
                          invoice.vendorAutomatedName ||
                          invoice.vendorName ||
                          "N/A")}
                      {["invoice"].includes(invoice.docType) &&
                        (invoice.customerEditedName ||
                          invoice.customerAutomatedName ||
                          invoice.customerName ||
                          "N/A")}
                    </small>
                  </div>
                </div>
              </div>
            </Alert>
          )}

          {/* Existing Matches Section */}
          {existingMatches.length > 0 && (
            <div className="mb-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0 text-primary">
                  <FontAwesomeIcon icon="link" className="mr-2" />
                  Existing Matches ({existingMatches.length})
                </h5>
                {isLoadingMatches && (
                  <FontAwesomeIcon icon="spinner" spin className="text-muted" />
                )}
              </div>
              <div
                style={{
                  maxHeight: "200px",
                  overflowY: "auto",
                  border: "1px solid #dee2e6",
                  borderRadius: "0.375rem",
                }}
              >
                <Table striped bordered hover size="sm" className="mb-0">
                  <thead style={{ backgroundColor: "#f8f9fa" }}>
                    <tr>
                      <th>Account</th>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existingMatches.map((match) => (
                      <tr key={match.id}>
                        <td>
                          {match.DestinationTransactionLine?.account || "N/A"}
                        </td>
                        <td>
                          {match.DestinationTransactionLine?.date || "N/A"}
                        </td>
                        <td>
                          {match.DestinationTransactionLine?.description ||
                            "N/A"}
                        </td>
                        <td>
                          $
                          {parseFloat(
                            match.DestinationTransactionLine?.amount || 0
                          ).toFixed(2)}
                        </td>
                        <td>
                          <Badge
                            variant={
                              match.status === "approved"
                                ? "success"
                                : match.status === "rejected"
                                ? "danger"
                                : "warning"
                            }
                          >
                            {match.status}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() =>
                              handleDeleteExistingMatch(
                                match.id,
                                match.bankExternalId
                              )
                            }
                          >
                            <FontAwesomeIcon icon="trash" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          {/* New Matches Section */}
          {selectedBankTransactions.length > 0 && (
            <div className="mb-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0 text-success">
                  <FontAwesomeIcon icon="plus-circle" className="mr-2" />
                  New Matches ({selectedBankTransactions.length})
                </h5>
              </div>
              <div
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: "0.375rem",
                }}
              >
                <Table striped bordered hover size="sm" className="mb-0">
                  <thead style={{ backgroundColor: "#f8f9fa" }}>
                    <tr>
                      <th>Account</th>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBankTransactions.map((transaction) => (
                      <tr key={transaction.id}>
                        <td>{transaction.account || "N/A"}</td>
                        <td>{transaction.date || "N/A"}</td>
                        <td>{transaction.description || "N/A"}</td>
                        <td>
                          ${parseFloat(transaction.amount || 0).toFixed(2)}
                        </td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() =>
                              handleRemoveTransaction(transaction.id)
                            }
                          >
                            <FontAwesomeIcon icon="trash" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {/* Action buttons for new matches */}
              <div
                className="d-flex justify-content-end mt-3"
                style={{ gap: "0.5rem" }}
              >
                <Button
                  variant="outline-secondary"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  <FontAwesomeIcon icon="times" className="mr-1" />
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCreateMatch}
                  disabled={isLoading || selectedBankTransactions.length === 0}
                >
                  {isLoading ? (
                    <>
                      <FontAwesomeIcon icon="spinner" spin className="mr-1" />
                      Creating Matches...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon="link" className="mr-1" />
                      Create Matches ({selectedBankTransactions.length})
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Available Bank Transactions */}
          <div className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="mb-0 text-primary">
                <FontAwesomeIcon icon="university" className="mr-2" />
                Available Bank Transactions
              </h5>
            </div>
            <div className="mb-3">
              <div className="d-flex align-items-center">
                <span className="mr-2 text-muted">Filter by:</span>
                {!providerMode ? (
                  <ExternalFilter
                    columnDefs={externalFilterColumns}
                    gridApi={gridApi.current}
                  />
                ) : (
                  <input
                    className="form-control form-control-sm"
                    style={{ maxWidth: 320 }}
                    placeholder="Search provider bank transactions"
                    value={providerSearchTerm}
                    onChange={(e) => {
                      const next = e.target.value;
                      setProviderSearchTerm(next);
                      searchProviderBankTransactions(next);
                    }}
                  />
                )}
              </div>
            </div>
            {!providerMode ? (
              <div
                style={{
                  height: "400px",
                  width: "100%",
                  border: "1px solid #dee2e6",
                  borderRadius: "0.375rem",
                }}
              >
                <AgBulkTable
                  onSetApi={(api) => (gridApi.current = api)}
                  columnDefs={columnDefs}
                  hasExport={false}
                  defaultColDef={defaultColDef}
                  rowSelection="multiple"
                  rowModelType="infinite"
                  pagination={true}
                  paginationPageSize={20}
                  height="400px"
                  width="100%"
                  cacheBlockSize={20}
                  rowData={[]}
                  renderBulkOps={(selectedRows) => (
                    <div className="mb-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleTransactionSelected(selectedRows)}
                        disabled={selectedRows.length === 0}
                      >
                        <FontAwesomeIcon icon="plus" className="mr-1" />
                        Add to Matches ({selectedRows.length})
                      </Button>
                    </div>
                  )}
                  bulkOpsClassName="d-flex justify-content-start"
                />
              </div>
            ) : (
              <div style={{ maxHeight: 400, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: "0.375rem" }}>
                <Table size="sm" bordered hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {providerSearching && (
                      <tr>
                        <td colSpan={5}>Searching...</td>
                      </tr>
                    )}
                    {!providerSearching &&
                      providerCandidates.map((tx: any) => (
                        <tr key={String(tx.id)}>
                          <td>{tx.account || "N/A"}</td>
                          <td>{tx.date || "N/A"}</td>
                          <td>{tx.description || "N/A"}</td>
                          <td>${parseFloat(tx.amount || 0).toFixed(2)}</td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              disabled={isLoading}
                              onClick={() => handleProviderSelect(tx)}
                            >
                              Select
                            </Button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        </div>

        {/* Drawer Footer */}
        {/* <div
          style={{
            padding: "1rem 1.5rem",
            borderTop: "1px solid #dee2e6",
            backgroundColor: "#f8f9fa",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <small className="text-muted">
            {existingMatches.length > 0 && (
              <>
                {existingMatches.length} existing match
                {existingMatches.length !== 1 ? "es" : ""}
              </>
            )}
            {existingMatches.length > 0 &&
              selectedBankTransactions.length > 0 &&
              " • "}
            {selectedBankTransactions.length > 0 && (
              <>
                {selectedBankTransactions.length} new match
                {selectedBankTransactions.length !== 1 ? "es" : ""} to create
              </>
            )}
            {existingMatches.length === 0 &&
              selectedBankTransactions.length === 0 &&
              "No matches created yet"}
          </small>
        </div> */}
      </div>
    </>
  );
};

export default InvoiceTransactionMatcher;

// Utility function to check if an invoice has matches
export const checkInvoiceMatches = async (
  invoiceTransactionLineId: number,
  integrationId?: number,
  integrationProvider?: "quickbooks" | "xero" | null
): Promise<{
  hasMatches: boolean;
  matchCount: number;
  matches: TransactionLineMatch[];
}> => {
  try {
    if (
      integrationId &&
      (integrationProvider === "quickbooks" || integrationProvider === "xero")
    ) {
      const response =
        integrationProvider === "quickbooks"
          ? await getQboBankTransactionMatch(
              Number(integrationId),
              invoiceTransactionLineId
            )
          : await getXeroBankTransactionMatch(
              Number(integrationId),
              invoiceTransactionLineId
            );
      const selectedMatches = Array.isArray(response?.selectedMatches)
        ? response.selectedMatches
        : [];
      const hasMatches = Boolean(response?.hasMatch) || selectedMatches.length > 0;
      const mappedMatches: TransactionLineMatch[] = selectedMatches.map(
        (m: any, idx: number) => ({
          id: idx + 1,
          sourceTransactionLineId: invoiceTransactionLineId,
          destinationTransactionLineId: 0,
          status: m.status || m.matchStatus || response?.matchStatus || "approved",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          bankExternalId: String(
            m.bankTransactionId || m.bankExternalId || m.bankTransactionID || ""
          ),
          DestinationTransactionLine: {
            account: m.accountName,
            date: m.date,
            description: m.description || m.payee || "",
            amount: m.amount,
          },
        })
      );
      return {
        hasMatches,
        matchCount: mappedMatches.length,
        matches: mappedMatches,
      };
    }

    const response = await getTransactionLineMatches(
      "invoice2bank",
      invoiceTransactionLineId
    );
    // Handle the API response format: { matches: [...] }
    const matches = response?.matches || [];
    return {
      hasMatches: matches && matches.length > 0,
      matchCount: matches ? matches.length : 0,
      matches: matches || [],
    };
  } catch (error) {
    console.error("Error checking invoice matches:", error);
    return {
      hasMatches: false,
      matchCount: 0,
      matches: [],
    };
  }
};

// Component to display match status indicator
export const InvoiceMatchStatus: React.FC<{
  invoiceTransactionLineId: number;
  className?: string;
  refreshTrigger?: number | string; // Add this prop to trigger refresh
  integrationId?: number;
  integrationProvider?: "quickbooks" | "xero" | null;
}> = ({
  invoiceTransactionLineId,
  className = "",
  refreshTrigger,
  integrationId,
  integrationProvider,
}) => {
  const [matchStatus, setMatchStatus] = useState<{
    hasMatches: boolean;
    matchCount: number;
    matches: TransactionLineMatch[];
  }>({
    hasMatches: false,
    matchCount: 0,
    matches: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadMatchStatus = async () => {
      setIsLoading(true);
      const status = await checkInvoiceMatches(
        invoiceTransactionLineId,
        integrationId,
        integrationProvider
      );
      setMatchStatus(status);
      setIsLoading(false);
    };

    if (invoiceTransactionLineId) {
      loadMatchStatus();
    }
  }, [invoiceTransactionLineId, refreshTrigger, integrationId, integrationProvider]);

  if (isLoading) {
    return (
      <Badge variant="secondary" className={className}>
        <FontAwesomeIcon icon="spinner" spin className="mr-1" />
        Loading...
      </Badge>
    );
  }

  if (!matchStatus.hasMatches) {
    return (
      <Badge variant="light" className={className}>
        <FontAwesomeIcon icon="unlink" className="mr-1" />
        No matches
      </Badge>
    );
  }

  const approvedCount = matchStatus.matches.filter(
    (m) => m.status === "approved"
  ).length;
  const rejectedCount = matchStatus.matches.filter(
    (m) => m.status === "rejected"
  ).length;
  const pendingCount = matchStatus.matches.filter(
    (m) => m.status === "pending"
  ).length;

  return (
    <Badge variant="success" className={className}>
      <FontAwesomeIcon icon="link" className="mr-1" />
      {matchStatus.matchCount} match{matchStatus.matchCount !== 1 ? "es" : ""}
    </Badge>
  );
};
